import React, { useEffect, useState } from 'react';

import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppLoading } from 'src/AppLoading';
import { handleLogout } from 'src/auth';
import { AlineAvatar, AlineButton, AlineTextarea } from 'src/components';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { RequestDocAccessImg } from 'src/components/icons/RequestDocAccess';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useRequestAccessToDocumentParams } from 'src/helpers';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    background: theme.palette.common.white,
    borderRadius: 20,
    boxShadow: '0px 12px 30px 0px rgba(50, 74, 82, 0.15)',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr 176px',
    padding: theme.spacing(5),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: 324,
  },
  currentUser: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 5),
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    justifyContent: 'center',
  },
  infoBox: {
    padding: theme.spacing(2, 0),
  },
  requestAccessImg: {
    left: 44,
    position: 'relative',
  },
  root: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
    width: '100%',
  },
  userDetails: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function RequestDocAccess(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { documentId } = useRequestAccessToDocumentParams();
  const [loggingOut, setLoggingOut] = useState<boolean>(false);

  const { checkingAccess } = useRedirectToDocumentIfAccessible(documentId);

  if (!currentUser) return <AppLoading waitingText={'Getting user info...'} />;
  if (checkingAccess) return <AppLoading waitingText={'Checking document access...'} />;

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.card}>
          <div className={classes.gridItem}>
            <AlineLogo />
            <RequestAccessBody documentId={documentId} />
          </div>
          <div className={classes.gridItem}>
            <RequestDocAccessImg className={classes.requestAccessImg} />
          </div>
        </div>
        <div className={classes.currentUser}>
          <Typography>You are logged in as:</Typography>
          <div className={classes.userDetails}>
            <AlineAvatar avatarInfo={currentUser} />
            <Typography>{currentUser?.displayName}</Typography>
            <AlineButton
              color="primary"
              loading={loggingOut}
              variant="outlined"
              onClick={async () => {
                setLoggingOut(true);
                await handleLogout();
                setLoggingOut(false);
              }}
            >
              Logout
            </AlineButton>
          </div>
        </div>
      </div>
    </div>
  );
}

interface RequestAccessProps {
  documentId: string;
  setRequestSent: ReactSetStateAction<boolean>;
}

function RequestAccessBody({ documentId }: Pick<RequestAccessProps, 'documentId'>): JSX.Element {
  const [requestSent, setRequestSent] = useState<boolean>(false);
  if (requestSent) return <RequestSent />;

  return <RequestAccess documentId={documentId} setRequestSent={setRequestSent} />;
}

function RequestAccess({ documentId, setRequestSent }: RequestAccessProps) {
  const { classes } = useStyles();
  const [message, setMessage] = useState<string>();
  const [requestAccessToStorable, { loading: requestingAccess }] =
    Apollo.useRequestAccessToStorableMutation();

  return (
    <div className={classes.container}>
      <Typography variant="h1">Request Access</Typography>
      <Typography>
        The owner of this document needs to approve your access before you can view or make edits.
      </Typography>
      <AlineTextarea
        maxRows={5}
        minRows={5}
        placeholder="Add a message for the document owner..."
        value={message}
        onChange={e => setMessage(e.target.value)}
      />
      <AlineButton
        color="primary"
        loading={requestingAccess}
        variant="contained"
        onClick={async () => {
          const { data } = await requestAccessToStorable({
            variables: {
              input: {
                message,
                storableIds: [documentId],
              },
            },
          });
          if (data?.requestAccessToStorable) setRequestSent(true);
        }}
      >
        Request Access
      </AlineButton>
    </div>
  );
}

function RequestSent() {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.infoBox}>
        <Typography variant="h1">Request sent</Typography>
        <Typography>The document owner has received your request and will respond soon.</Typography>
      </div>
      <AlineButton color="primary" href={link.DashboardIndex()} variant="outlined">
        Go to Dashboard
      </AlineButton>
    </div>
  );
}

/**
 * Redirect to document if user can access it.
 */
function useRedirectToDocumentIfAccessible(documentId: string) {
  const navigate = useNavigate();
  const { data, loading: checkingAccess } = Apollo.useGetRequesterDocumentAccessQuery({
    variables: { documentId },
  });

  useEffect(() => {
    if (checkingAccess) return;
    const userHasDocumentAccess = Boolean(data?.document);
    if (userHasDocumentAccess) {
      navigate(link.DocumentDetail({ documentId }));
    }
  }, [documentId, navigate, data, checkingAccess]);

  return { checkingAccess };
}
